import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search, X, Lock, ChevronDown } from "lucide-react";
import TaxonomyFlag from "@/components/TaxonomyFlag";

const API = `/api`;

export default function Ressources() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("tous");
  const [limit, setLimit] = useState(24);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(params.get("q") || "");
  const q = (params.get("q") || "").toLowerCase();
  const tag = params.get("tag");
  const cat = params.get("cat");
  const pole = params.get("pole") || "";
  const theme = params.get("theme") || "";
  const [taxonomy, setTaxonomy] = useState(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState("");
  const [sources, setSources] = useState([]);

  const ask = async (e) => {
    e.preventDefault();
    if (!question.trim() || asking) return;
    setAsking(true);
    setAnswer("");
    setSources([]);
    try {
      const resp = await fetch(`${API}/knowledge/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: question.trim() }),
      });
      if (resp.status === 429) {
        setAnswer(
          "Vous avez atteint la limite de questions pour cette heure. Réessayez plus tard, ou contactez directement le cabinet pour une réponse personnalisée."
        );
        setAsking(false);
        return;
      }
      if (!resp.ok || !resp.body) throw new Error("ask failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          try {
            const data = JSON.parse(p.slice(5));
            if (data.type === "sources") setSources(data.items);
            else if (data.type === "delta") setAnswer((a) => a + data.text);
          } catch {}
        }
      }
    } catch {
      setAnswer("La génération de la réponse a échoué. Réessayez ou contactez le cabinet.");
    }
    setAsking(false);
  };

  useEffect(() => {
    fetch(`${API}/knowledge`)
      .then((r) => r.json())
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    fetch(`${API}/knowledge/taxonomy`)
      .then((r) => r.json())
      .then(setTaxonomy)
      .catch(() => {});
  }, []);

  const types = [...new Set(items.map((i) => i.type))];
  const labelOf = (t) => items.find((i) => i.type === t)?.type_label || t;
  const searched = q
    ? items.filter((i) => `${i.title} ${i.excerpt} ${(i.tags || []).join(" ")}`.toLowerCase().includes(q))
    : items;
  const tagged = tag ? searched.filter((i) => (i.tags || []).includes(tag)) : searched;
  const categorized = cat ? tagged.filter((i) => (i.categories || []).includes(cat)) : tagged;
  const poled = pole ? categorized.filter((i) => (i.poles || []).includes(pole)) : categorized;
  const themed = theme ? poled.filter((i) => (i.themes || []).includes(theme)) : poled;
  const visible = filter === "tous" ? themed : themed.filter((i) => i.type === filter);
  const shown = visible.slice(0, limit);

  const countIn = (list, field, key) =>
    list.filter((i) => (i[field] || []).includes(key)).length;

  const setParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (!value) p.delete(key);
    else p.set(key, value);
    setParams(p);
  };

  const clearParam = (key) => {
    const p = new URLSearchParams(params);
    p.delete(key);
    setParams(p);
    if (key === "q") setSearchInput("");
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) setParams({ q: searchInput.trim() });
  };

  const clearSearch = () => {
    setSearchInput("");
    setParams({});
  };

  return (
    <div className="pt-40 pb-24 sm:pb-32 min-h-screen" data-testid="ressources-page">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="label-accent" data-testid="ressources-overline">Knowledge portal</span>
          <h1 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Ressources &{" "}
            <em className="italic text-gold-gradient not-italic">fiches pratiques.</em>
          </h1>
          <p className="mt-6 text-sm sm:text-base text-[#A0A5B1] max-w-2xl leading-relaxed">
            Concepts juridiques, clauses et procédures — la base de connaissances du cabinet,
            en droit belge.
          </p>
          <Link
            to="/ressources/a-propos"
            data-testid="ressources-apropos-link"
            className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold/80 hover:text-gold transition-colors border-b border-gold/25 hover:border-gold/60 pb-1"
          >
            En savoir plus sur le projet <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <p className="mt-4 inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70 border border-gold/25 px-3 py-2" data-testid="ressources-sample-badge">
            Migration complète — {items.length} fiches reprises de WordPress via API
          </p>
          <p className="mt-4 text-xs text-[#626773] leading-relaxed max-w-2xl" data-testid="ressources-terms-notice">
            Fiches fournies à titre informatif — en consultant ce portail, vous acceptez nos{" "}
            <Link
              to="/conditions-utilisation-portail"
              data-testid="ressources-terms-link"
              className="text-gold/90 underline decoration-gold/40 underline-offset-4 hover:text-gold"
            >
              conditions d'utilisation de la base de connaissance
            </Link>
            .
          </p>
        </motion.div>

        <div className="mt-12 bg-ink-card border border-gold/20 p-8 sm:p-10" data-testid="ask-ai-block">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="label-accent">Posez votre question</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/60 border border-gold/20 px-2.5 py-1">
              Réponse IA basée sur les fiches — à valider par le cabinet
            </span>
          </div>
          <form onSubmit={ask} className="mt-6 flex flex-col sm:flex-row gap-4">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ex. : comment protéger mes actions en cas de conflit entre associés ?"
              data-testid="ask-ai-input"
              className="flex-1 bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3.5 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors"
            />
            <button
              type="submit"
              disabled={asking}
              data-testid="ask-ai-button"
              className="bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-3.5 hover:bg-gold-light transition-colors disabled:opacity-50 shrink-0"
            >
              {asking ? "Réflexion…" : "Interroger"}
            </button>
          </form>

          <div className="mt-5 flex items-start gap-3" data-testid="ask-ai-privacy-notice">
            <Lock className="w-4 h-4 text-gold/70 shrink-0 mt-0.5" />
            <p className="text-xs text-[#626773] leading-relaxed">
              Ne saisissez aucune information confidentielle ou nominative : ce service automatisé
              n'est pas couvert par le secret professionnel et votre question n'est pas conservée.
              Pour toute question confidentielle,{" "}
              <a href="/#contact" className="text-gold/90 underline decoration-gold/40 underline-offset-4 hover:text-gold">
                contactez directement le cabinet
              </a>{" "}
              — vos échanges avec un avocat sont, eux, couverts par le secret professionnel.
            </p>
          </div>

          {(answer || sources.length > 0) && (
            <div className="mt-8 pt-8 border-t border-white/10" data-testid="ask-ai-result">
              {answer && (
                <p className="text-sm sm:text-base text-[#A0A5B1] leading-relaxed whitespace-pre-wrap" data-testid="ask-ai-answer">
                  {answer.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                    part.startsWith("**") && part.endsWith("**") ? (
                      <strong key={i} className="text-[#FAF8F5] font-medium">{part.slice(2, -2)}</strong>
                    ) : (
                      part
                    )
                  )}
                  {asking && <span className="inline-block w-2 h-4 bg-gold ml-1 animate-pulse" />}
                </p>
              )}
              {sources.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2" data-testid="ask-ai-sources">
                  {sources.map((s) => (
                    <Link
                      key={`${s.type}-${s.slug}`}
                      to={`/ressources/${s.type}/${s.slug}`}
                      data-testid={`ask-source-${s.slug}`}
                      className="text-xs text-gold/80 border border-gold/25 px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors"
                    >
                      {s.title}
                    </Link>
                  ))}
                </div>
              )}
              {!asking && answer && (
                <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4" data-testid="ask-ai-cta">
                  <p className="text-sm text-[#A0A5B1]">
                    Votre situation mérite une réponse personnalisée, couverte par le secret professionnel.
                  </p>
                  <a
                    href="/#contact"
                    data-testid="ask-ai-contact-cta"
                    className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-6 py-3.5 hover:bg-gold-light transition-colors shrink-0"
                  >
                    Échanger avec un avocat
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        <form onSubmit={submitSearch} className="mt-12 flex max-w-xl" data-testid="ressources-search-form">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher dans les fiches…"
            data-testid="ressources-search-input"
            className="flex-1 bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors"
          />
          <button
            type="submit"
            data-testid="ressources-search-button"
            className="bg-gold text-ink px-5 hover:bg-gold-light transition-colors"
            aria-label="Rechercher"
          >
            <Search className="w-4 h-4" />
          </button>
        </form>

        {q && (
          <div className="mt-6 flex items-center gap-4" data-testid="search-results-info">
            <p className="text-sm text-[#A0A5B1]">
              {visible.length} résultat{visible.length > 1 ? "s" : ""} pour «{" "}
              <span className="text-gold">{params.get("q")}</span> »
            </p>
            <button
              onClick={() => clearParam("q")}
              data-testid="search-clear-button"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#626773] hover:text-gold transition-colors"
            >
              <X className="w-3.5 h-3.5" /> Effacer
            </button>
          </div>
        )}

        {(tag || cat || filter !== "tous") && (
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="active-filters">
            {tag && (
              <button
                onClick={() => clearParam("tag")}
                data-testid="active-tag-filter"
                className="inline-flex items-center gap-2 text-xs text-gold border border-gold/40 px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors"
              >
                Étiquette : {tag} <X className="w-3 h-3" />
              </button>
            )}
            {cat && (
              <button
                onClick={() => clearParam("cat")}
                data-testid="active-cat-filter"
                className="inline-flex items-center gap-2 text-xs text-[#A0A5B1] border border-white/15 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors"
              >
                Catégorie : {cat} <X className="w-3 h-3" />
              </button>
            )}
            {filter !== "tous" && (
              <button
                onClick={() => setFilter("tous")}
                data-testid="active-type-filter"
                className="inline-flex items-center gap-2 text-xs text-[#A0A5B1] border border-white/15 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors"
              >
                Type : {labelOf(filter)} <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {taxonomy && (
          <div className="mt-12" data-testid="pole-filters">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="label-accent">Explorer par pôle d'expertise</span>
              {pole && (
                <button
                  onClick={() => setParam("pole", "")}
                  data-testid="pole-filter-all"
                  className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626773] hover:text-gold transition-colors"
                >
                  Tous les pôles <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {taxonomy.poles.map((p) => {
                const active = pole === p.key;
                const n = countIn(categorized, "poles", p.key);
                return (
                  <button
                    key={p.key}
                    onClick={() => setParam("pole", active ? "" : p.key)}
                    data-testid={`pole-filter-${p.key}`}
                    className={`group text-left border p-6 sm:p-7 transition-colors duration-300 ${
                      active
                        ? "border-gold bg-gold/10"
                        : "border-white/10 bg-ink-card hover:border-gold/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-mono text-[10px] tracking-[0.25em] text-gold/70">{p.code}</span>
                      <span className="font-mono text-[10px] tracking-[0.15em] text-[#626773]">
                        {n} fiche{n > 1 ? "s" : ""}
                      </span>
                    </div>
                    <span className={`mt-4 block font-serif text-xl leading-snug transition-colors duration-300 ${active ? "text-gold-light" : "group-hover:text-gold-light"}`}>
                      {p.label}
                    </span>
                    <span className="mt-2 block text-xs text-[#A0A5B1] leading-relaxed">{p.tagline}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8" data-testid="theme-filters">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#626773]">Thèmes</span>
              <div className="mt-4 flex flex-wrap gap-2">
                {taxonomy.themes.map((t) => {
                  const active = theme === t.key;
                  const n = countIn(poled, "themes", t.key);
                  if (n === 0 && !active) return null;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setParam("theme", active ? "" : t.key)}
                      data-testid={`theme-filter-${t.key}`}
                      className={`text-[10px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors duration-300 ${
                        active
                          ? "bg-gold text-ink border-gold font-medium"
                          : "border-white/15 text-[#A0A5B1] hover:border-gold/60 hover:text-gold"
                      }`}
                    >
                      {t.label} · {n}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8" data-testid="type-filters-block">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <button
              onClick={() => setTypesOpen(!typesOpen)}
              data-testid="type-filter-toggle"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#626773] hover:text-gold transition-colors"
            >
              Type de fiche
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${typesOpen ? "rotate-180" : ""}`} />
            </button>
            <Link
              to="/comparer"
              data-testid="compare-entry"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70 hover:text-gold transition-colors"
            >
              Comparer les formes de sociétés <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {typesOpen && (
            <div className="mt-4 flex flex-wrap gap-3" data-testid="ressources-filters">
              <button
                onClick={() => setFilter("tous")}
                data-testid="filter-tous"
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  filter === "tous"
                    ? "bg-gold text-ink border-gold font-medium"
                    : "border-white/15 text-[#A0A5B1] hover:border-gold/60 hover:text-gold"
                }`}
              >
                Tous
              </button>
              {types.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  data-testid={`filter-${t}`}
                  className={`px-5 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                    filter === t
                      ? "bg-gold text-ink border-gold font-medium"
                      : "border-white/15 text-[#A0A5B1] hover:border-gold/60 hover:text-gold"
                  }`}
                >
                  {labelOf(t)}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <p className="mt-20 text-[#626773] font-mono text-sm" data-testid="ressources-loading">
            Chargement des fiches…
          </p>
        ) : visible.length === 0 ? (
          <p className="mt-20 font-serif text-2xl text-[#A0A5B1]" data-testid="ressources-empty">
            Aucune fiche ne correspond à cette recherche.
          </p>
        ) : (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shown.map((item, i) => (
              <motion.div
                key={`${item.type}-${item.slug}`}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <TaxonomyFlag
                  item={item}
                  taxonomy={taxonomy}
                  activePole={pole}
                  activeTheme={theme}
                  onChanged={(it, p, t, r) =>
                    setItems((list) =>
                      list.map((x) =>
                        x.type === it.type && x.slug === it.slug
                          ? { ...x, poles: p, themes: t, needs_review: r }
                          : x
                      )
                    )
                  }
                />
                <Link
                  to={`/ressources/${item.type}/${item.slug}`}
                  data-testid={`fiche-card-${item.slug}`}
                  className="group block bg-ink-card border border-white/5 hover:border-gold/40 p-8 transition-colors duration-500 h-full"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-gold/70 uppercase">
                      {item.type_label}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-[#626773] group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                  </div>
                  <h2 className="mt-6 font-serif text-xl sm:text-2xl leading-snug group-hover:text-gold-light transition-colors duration-300">
                    {item.title}
                  </h2>
                  <p className="mt-4 text-sm text-[#A0A5B1] leading-relaxed line-clamp-3">
                    {item.excerpt}
                  </p>
                  <span className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-gold/80">
                    Lire la fiche
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {!loading && visible.length > limit && (
          <div className="mt-12 text-center">
            <button
              onClick={() => setLimit(limit + 24)}
              data-testid="load-more-button"
              className="border border-gold/40 text-gold text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-gold/10 transition-colors duration-300"
            >
              Afficher plus ({visible.length - limit} restantes)
            </button>
          </div>
        )}

        <div className="mt-20 bg-ink-card border border-gold/20 p-8 sm:p-12 flex flex-wrap items-center justify-between gap-6" data-testid="ressources-cta">
          <div className="max-w-xl">
            <span className="label-accent">Une situation particulière ?</span>
            <p className="mt-4 font-serif text-2xl sm:text-3xl leading-snug">
              Chaque dossier est unique.{" "}
              <em className="italic text-gold-gradient not-italic">Un avocat vous répond personnellement.</em>
            </p>
            <p className="mt-3 text-sm text-[#A0A5B1] leading-relaxed">
              Vos échanges avec le cabinet sont couverts par le secret professionnel —
              premier échange sans engagement.
            </p>
          </div>
          <a
            href="/#contact"
            data-testid="ressources-cta-contact"
            className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-4 hover:bg-gold-light transition-colors shrink-0"
          >
            Échanger avec un avocat
          </a>
        </div>
      </div>
    </div>
  );
}
